import { useCallback, useMemo } from 'react';

import { BannerActionType, type ExternalOfferFragment } from '@ocx/graphql';

import { useActivateExternalBanner } from './use-activate-external-banner.hook';

export const isActionAvailable = (startsAt?: string | null, expiresAt?: string | null): boolean => {
  const now = Date.now();
  let starts = startsAt ? new Date(startsAt).getTime() : null;
  let expires = expiresAt ? new Date(expiresAt).getTime() : null;

  if (starts !== null && Number.isNaN(starts)) {
    starts = null;
  }
  if (expires !== null && Number.isNaN(expires)) {
    expires = null;
  }

  return (starts === null || starts <= now) && (expires === null || now <= expires);
};

export type ExternalBannerAction = () => Promise<unknown>;

export type ExternalBannerActions = Partial<
  Record<
    BannerActionType,
    {
      type: BannerActionType;
      onAction: ExternalBannerAction;
      expiresAt: string;
      inProgress: boolean;
    }
  >
>;

export const useGetExternalBannerActions = (banner: ExternalOfferFragment): ExternalBannerActions => {
  const { actions } = banner;
  const { activate, isActivating } = useActivateExternalBanner();

  // Not memoized: result depends on Date.now(), so it must be recalculated on each render
  const activateAction = (actions ?? []).filter((action) => {
    return action?.type === BannerActionType.Activate && isActionAvailable(action.startsAt, action.expiresAt);
  })[0];

  const onActivate = useCallback(() => {
    return activate(banner);
  }, [activate, banner]);

  return useMemo(() => {
    if (!activateAction) {
      return {};
    }

    return {
      [BannerActionType.Activate]: {
        type: BannerActionType.Activate,
        onAction: onActivate,
        expiresAt: activateAction.expiresAt,
        inProgress: isActivating,
      },
    };
  }, [activateAction, isActivating, onActivate]);
};
